import { useNavigate } from "react-router-dom";
import { ChevronLeft, Trash2, Mail, CheckCircle2, Clock, AlertCircle } from "lucide-react";

const STEPS = [
  {
    icon: Mail,
    title: "Submit a Deletion Request",
    desc: 'Send an email to privacy@exqura.com with the subject line "Data Deletion Request". Include the email address linked to your Exqura account and, if applicable, confirm you connected via Facebook Login.',
  },
  {
    icon: CheckCircle2,
    title: "We Verify Your Identity",
    desc: "We will verify that the request is coming from the account owner. You may be asked to confirm your email address or provide additional identification.",
  },
  {
    icon: Trash2,
    title: "Data Is Deleted",
    desc: "Upon verification, we will permanently delete or anonymise your personal data from our systems within 30 days. This includes your profile, bookings, passport details, and any data received from Facebook.",
  },
  {
    icon: Clock,
    title: "Confirmation Sent",
    desc: "You will receive an email confirmation once the deletion has been completed. Some records may be retained for up to 90 days in encrypted backups before being purged.",
  },
];

const WHAT_IS_DELETED = [
  "Account information (name, email, profile photo)",
  "Facebook profile data linked to your account",
  "Passport and travel document details",
  "Golf booking, ferry, transport, and accommodation history",
  "Preferences and personalisation data",
  "Device and usage logs associated with your account",
];

const WHAT_IS_RETAINED = [
  "Financial transaction records required by law (up to 7 years)",
  "Anonymised, aggregated usage statistics that cannot identify you",
  "Records needed to resolve ongoing disputes or enforce our terms",
];

export default function DataDeletion() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0B2421]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-6 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-widest">Legal</p>
          </div>
          <h1 className="text-white font-bold text-3xl sm:text-4xl mb-2">Data Deletion</h1>
          <p className="text-white/50 text-sm">How to request deletion of your personal data from Exqura</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Intro */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
          <p className="text-gray-600 leading-relaxed">
            At <span className="font-semibold text-[#0B2421]">Exqura Golf & Experiences</span>, you have the right to request deletion of your personal
            data at any time. This page explains what data we hold, what gets deleted, what may be retained for legal reasons, and how to submit a request.
          </p>
        </div>

        {/* Facebook notice */}
        <div className="bg-[#1877F2]/8 border border-[#1877F2]/20 rounded-2xl p-5 flex gap-4">
          <AlertCircle className="h-5 w-5 text-[#1877F2] shrink-0 mt-0.5" />
          <div>
            <p className="text-[#0B2421] font-semibold text-sm mb-1">Facebook Login Users</p>
            <p className="text-gray-600 text-sm leading-relaxed">
              If you signed in using Facebook Login, you can also remove Exqura's access to your Facebook data directly from your Facebook account:
              Go to <span className="font-medium">Facebook Settings → Apps and Websites → Exqura → Remove</span>.
              This revokes Facebook's permission grant. To delete data already stored by Exqura, please also follow the steps below.
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
          <h2 className="text-[#0B2421] font-bold text-xl mb-6">How to Request Data Deletion</h2>
          <div className="space-y-6">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="flex gap-4">
                  <div className="shrink-0 flex flex-col items-center">
                    <div className="w-10 h-10 rounded-xl bg-[#0B2421] flex items-center justify-center">
                      <Icon className="h-4 w-4 text-[#D4AF37]" />
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className="w-px flex-1 bg-gray-200 mt-2 min-h-6" />
                    )}
                  </div>
                  <div className="pb-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-[#D4AF37] uppercase tracking-widest">Step {i + 1}</span>
                    </div>
                    <h3 className="font-semibold text-[#0B2421] mb-1">{step.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* What gets deleted / retained */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <Trash2 className="h-4 w-4 text-red-500" />
              <h2 className="text-[#0B2421] font-bold text-base">What Gets Deleted</h2>
            </div>
            <ul className="space-y-2">
              {WHAT_IS_DELETED.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-4 w-4 text-amber-500" />
              <h2 className="text-[#0B2421] font-bold text-base">What May Be Retained</h2>
            </div>
            <ul className="space-y-2">
              {WHAT_IS_RETAINED.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                  <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="bg-[#0B2421] rounded-2xl p-6 sm:p-8 text-center">
          <h2 className="text-white font-bold text-xl mb-2">Ready to Submit a Request?</h2>
          <p className="text-white/60 text-sm mb-5">
            Email us and we will process your deletion within 30 days.
          </p>
          <a
            href="mailto:privacy@exqura.com?subject=Data%20Deletion%20Request"
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] font-bold rounded-xl px-6 py-3 transition-colors text-sm"
          >
            <Mail className="h-4 w-4" />
            privacy@exqura.com
          </a>
        </div>

        <p className="text-center text-gray-400 text-xs pb-8">
          © {new Date().getFullYear()} Exqura Golf & Experiences. All rights reserved.
        </p>
      </div>
    </div>
  );
}
